import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {environment} from 'src/environments/environment';
import {ToastService} from './toast.service';
import decode from 'jwt-decode';
import {PrincipalService} from './principal.service';

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    /**
     * Key access token session storage
     * @private
     */
    private readonly KEY_ACCESS_TOKEN = 'access_token';

    /**
     * Key access token session storage
     * @private
     */
    private readonly KEY_NEW_ACCESS_TOKEN = 'new_access_token';

    /**
     * Key email to access in session storage
     * @private
     */
    private readonly KEY_EMAIL = 'email';

    /**
     * Key access company session storage
     * @private
     */
    private readonly KEY_COMPANY = 'company';

    /**
     * Key access require complete registration
     * @private
     */
    private readonly KEY_COMPLETE_REGISTRATION = 'complete_registration';

    /**
     * API URL
     * @private
     */
    private readonly apiUrl = environment.apiUrl;

    /**
     * New API URL
     * @private
     */
    private readonly newAPIUrl = environment.newApiUrl;

    /**
     * Constructor
     * @param http - Angular Http Client
     * @param toast - Toast service
     * @param principal - Principal service
     */
    constructor(
        private http: HttpClient,
        private toast: ToastService,
        private principal: PrincipalService
    ) {
    }

    /**
     * POST: Request auth user
     * @param data - Auth auth data
     */
    public login(data: {
        login: string;
        senha: string;
        captcha: string;
    }): Observable<any> {
        return this.http.post(`${this.apiUrl}/login`, data);
    }

    /**
     * POST: Request auth user
     * @param data - Auth auth data
     */
    public newLogin(data: {
        login: string;
        password: string;
    }): Observable<any> {
        return this.http.post(`${this.newAPIUrl}/auth/login`, data);
    }

    /**
    * POST: Request cognito token
    * @param data - Auth cognito token
    */
    public loginCognito(data: {
        cogTk: string;
    }): Observable<any> {
        return this.http.post(`${this.newAPIUrl}/auth/cognitoLogin`, data);
    }

    /**
     * PUT: Request forgot password
     * @param data - Forgot password data
     */
    public forgotPassoword(data: {
        email: string;
        captcha: string;
    }): Observable<any> {
        return this.http.put(`${this.apiUrl}/login/senha/esqueceu`, data);
    }

    /**
     * POST: Request signup user
     * @param data - User data signup
     */
    public signUp(data: any): Observable<any> {
        return this.http.post(`${this.apiUrl}/signup`, data);
    }

    /**
     * Save access token in session storage
     * @param response - Response login data
     */
    public saveAuthData(response: { token: string; empresas: any; completarCadastroEmpresa: boolean }): void {
        sessionStorage.setItem(this.KEY_ACCESS_TOKEN, response.token);
        sessionStorage.setItem(
            this.KEY_COMPANY,
            JSON.stringify(response.empresas[0])
        );
        this.setCompleteRegistration(response.completarCadastroEmpresa);
    }

    /**
     * Save access token in session storage
     * @param response - Response login data
     */
    public saveNewAuthData(response: { token: string }): void {
        sessionStorage.setItem(this.KEY_NEW_ACCESS_TOKEN, response.token);
    }

    /**
     * Return complete registration status
     */
    public getCompleteRegistration(): boolean {
        return sessionStorage.getItem(this.KEY_COMPLETE_REGISTRATION) === 'T';
    }

    /**
     * Return complete registration status
     */
    public setCompleteRegistration(value: boolean) {
        sessionStorage.setItem(this.KEY_COMPLETE_REGISTRATION, value ? 'T' : 'F');
    }

    /**
     * Return access token from session storage
     */
    public getAccessToken(): string {
        return sessionStorage.getItem(this.KEY_ACCESS_TOKEN);
    }

    /**
     * Return access token from session storage
     */
    public getNewAccessToken(): string {
        return sessionStorage.getItem(this.KEY_NEW_ACCESS_TOKEN);
    }

    public getApiUserId(): string {
        return (decode(sessionStorage.getItem(this.KEY_NEW_ACCESS_TOKEN)) as any)?.id;
    }

    /**
     * Return access company from session storage
     */
    public getCompany(): any {
        return JSON.parse(sessionStorage.getItem(this.KEY_COMPANY));
    }

    /**
     * Return email from session storage
     */
    public getEmail(): string {
        return sessionStorage.getItem(this.KEY_EMAIL);
    }

    /**
     * Update email to session storage
     */
    public setEmail(value: string) {
        return sessionStorage.setItem(this.KEY_EMAIL, value);
    }

    /**
     * Logoff user - clear storage date
     */
    public logoff(): void {
        localStorage.clear();
        sessionStorage.clear();
        this.principal.clearAll();
    }

    /**
     * Check if user has some permissions
     */
    hasPermission(permissions: any[]): boolean {
        let hasPermission = false;
        if (permissions && permissions.length > 0) {
            if (this.getCompleteRegistration()) {
                return false;
            }
            const decodedToken: any = decode(this.getAccessToken());
            permissions.forEach(permission => {
                if (decodedToken.userdata?.find(p => p.toLowerCase() === permission)) {
                    hasPermission = true;
                }
            });
            return hasPermission;
        } else {
            return true;
        }
    }
}
